import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isEditor } from "@/lib/roles";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { format, addMonths, subMonths } from "date-fns";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { toast } from "sonner";
import { CalendarGrid } from "@/components/calendar/CalendarGrid";
import { UpcomingList } from "@/components/calendar/UpcomingList";
import { EventDialog } from "@/components/calendar/EventDialog";
import { BLANK_EVENT } from "@/components/calendar/constants";

export default function Calendar() {
  const { user } = useAuth();
  const editor = isEditor(user?.role);
  const [cursor, setCursor] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK_EVENT);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => api.get("/events").then((r) => setEvents(r.data)).catch((e) => console.error("events load", e)), []);
  useEffect(() => { load(); }, [load]);

  const openCreate = (day) => {
    if (!editor) return;
    setEditing(null);
    setForm({ ...BLANK_EVENT, date: format(day || cursor, "yyyy-MM-dd") });
    setOpen(true);
  };
  const openEdit = (e) => {
    setEditing(e);
    setForm({ title: e.title, type: e.type, date: e.date, time: e.time || "", location: e.location || "", owner: e.owner || "" });
    setOpen(true);
  };
  const close = () => { setOpen(false); setEditing(null); };

  const save = async () => {
    if (!form.title || !form.date) return toast.error("Title and date are required");
    try {
      if (editing) await api.put(`/events/${editing.id}`, form);
      else await api.post("/events", form);
      toast.success(editing ? "Event updated" : "Event added");
      close(); setForm(BLANK_EVENT); load();
    } catch (e) { console.error("save event", e); toast.error("Could not save (editor access required)"); }
  };
  const remove = async () => {
    if (!editing) return;
    try { await api.delete(`/events/${editing.id}`); toast.success("Event deleted"); close(); load(); }
    catch (e) { console.error("delete event", e); toast.error("Could not delete event"); }
  };

  return (
    <div data-testid="calendar-page">
      <PageHeader overline="Firm Hub" title="Firm Calendar" subtitle="Hearings, trials, CLE deadlines and firm events — add, edit and keep everyone in sync.">
        {editor && <Button data-testid="calendar-add-btn" onClick={() => openCreate(null)}><Plus className="h-4 w-4 mr-1.5" /> Add Event</Button>}
      </PageHeader>

      <div className="grid lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-xl">{format(cursor, "MMMM yyyy")}</h2>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" data-testid="cal-today" onClick={() => setCursor(new Date())}>Today</Button>
              <Button variant="outline" size="icon" className="h-9 w-9" data-testid="cal-prev" onClick={() => setCursor(subMonths(cursor, 1))}><ChevronLeft className="h-4 w-4" /></Button>
              <Button variant="outline" size="icon" className="h-9 w-9" data-testid="cal-next" onClick={() => setCursor(addMonths(cursor, 1))}><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
          <CalendarGrid cursor={cursor} events={events} editor={editor} onDayClick={openCreate} onEventClick={openEdit} />
        </div>
        <UpcomingList events={events} onOpen={openEdit} />
      </div>

      <EventDialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }} editing={editing} form={form} setForm={setForm} onSave={save} onDelete={remove} />
    </div>
  );
}
